import EditIcon from '@mui/icons-material/Edit';
import { alpha, Box, Collapse, Stack, Typography } from '@mui/material';
import { DateRange } from '@mui/x-date-pickers-pro';
import { tzdate, TZDate } from '@repo/tzdate';
import { Fragment, useEffect, useState } from 'react';
import { useAtom } from 'ximple';
import MultipleNumberInput from 'src/components/common/MultipleNumberInput/MultipleNumberInput';
import { useOnClickBook } from 'src/hooks/domain/activities/useOnClickBook';
import { useLocale } from '@repo/i18n';
import { useCustomizations } from 'src/components/utils/theme/customizations';
import { TimeSlotType } from '@repo/widget-utils/TimeSlotType';
import { WarningMessage } from '../booking-base/BookingCardWarningMessage';
import { ProductInstance } from '@repo/types';
import InstancePicker from '../instance-picker/InstancePicker';
import { Dropdown } from 'src/components/common/dropdown/Dropdown';
import { InlineSummary } from '../summary/InlineSummary';
import { cartAtom, selectIsProductInCart } from 'src/state/cart/cartAtom';
import {
    productDisablePaymentPlans,
    productInstancesHasDepositPrices,
} from '@repo/widget-utils/cart/cartUtils';
import { useConfigurations } from '@repo/widget-utils/widgetsConfiguration';
import usePriceQuantities from 'src/hooks/domain/cart/usePriceQuantities';
import useTicketTypesWithinProduct from 'src/hooks/domain/cart/useTicketTypesWithinProduct';
import useUpdateNewCartItem from 'src/hooks/domain/cart/useUpdateNewCartItem';
import {
    setHasChosenDate,
    setQuantities,
    setSelectedProducts,
    setSelectedTimeSlot,
    setSelectedTicketType,
} from 'src/state/booking/bookingReducer';
import { useBookingContext } from 'src/widgets/BookingContext';
import { useNativeBookingCoverage } from 'src/hooks/domain/native-membership/useNativeBookingCoverage';
import InCartIndicator from 'src/components/domain/cart/InCartIndicator';
import { useActivityQuantities } from 'src/hooks/domain/booking/useActivityQuantities';

export function TourProductCardBookingForm(props: {
    products: ProductInstance[];
    displayDate?: boolean;
    displayTimeBorder?: boolean;
    hideTime?: boolean;
}) {
    const { products, displayDate, displayTimeBorder = true, hideTime } = props;
    const hasMultipleProducts = props.products.length > 1;
    const hasAvailableCapacity = products.some(
        (product) =>
            (product.capacity > 0 && !product.isClosedForBooking) ||
            (product.hasWaitingList && product.isClosedForBooking),
    );
    const product = props.products?.[0]?.product;

    const { t } = useLocale();
    const customizations = useCustomizations();
    const configuration = useConfigurations();

    const [cartItems] = useAtom(cartAtom);
    const inCart =
        product && !hasMultipleProducts ? selectIsProductInCart(cartItems, product.id) : false;
    const {
        quantities,
        selectedProducts,
        selectedTimeSlot,
        selectedTicketType,
        isLoadingPrices,
        hasChosenDate,
        attemptedBooking,
        bookingDispatch,
    } = useBookingContext();

    const [, setDateRange] = useState<DateRange<TZDate>>([null, null]);

    const [currentInteraction, setCurrentInteraction] = useState<
        'SELECT_PARTICIPANTS' | 'SELECT_TIME' | undefined
    >(hasMultipleProducts ? 'SELECT_TIME' : 'SELECT_PARTICIPANTS');

    useEffect(() => {
        if (!hasMultipleProducts) {
            bookingDispatch(setHasChosenDate(true));
            setCurrentInteraction('SELECT_PARTICIPANTS');
            bookingDispatch(
                setSelectedTimeSlot({
                    label: products[0].start.format('HH:mm'),
                    product: products[0],
                }),
            );
        }
    }, [setCurrentInteraction, hasMultipleProducts, products, bookingDispatch]);

    const hasDepositPrices = productInstancesHasDepositPrices(products);
    const newItemDisablePaymentPlans = productDisablePaymentPlans(
        product,
        false,
        hasDepositPrices,
        configuration.disableMembershipBooking,
    );
    useUpdateNewCartItem(newItemDisablePaymentPlans);

    const ticketTypes = useTicketTypesWithinProduct(product);
    const priceQuantities = usePriceQuantities(product, selectedProducts, quantities);
    const { discountedTotal, isLoading: isCoverageLoading } = useNativeBookingCoverage();

    useEffect(() => {
        if (ticketTypes.length === 1) {
            bookingDispatch(setSelectedTicketType(ticketTypes[0]));
        } else if (ticketTypes.length > 0) {
            bookingDispatch(
                setSelectedTicketType(
                    selectedTicketType
                        ? ticketTypes.find((t) => t.id === selectedTicketType.id)
                        : undefined,
                ),
            );
        }
    }, [selectedTicketType, ticketTypes, bookingDispatch]);

    const onClickBook = useOnClickBook(priceQuantities, product, setDateRange, true);

    const onBook = () => {
        setCurrentInteraction(hasMultipleProducts ? 'SELECT_TIME' : 'SELECT_PARTICIPANTS');
        onClickBook();
    };

    // Use new domain hook for activity quantities
    const { inputItems, errors, onChangeQuantity } = useActivityQuantities();

    // Split errors for display
    const totalErrors = errors.filter((e) => e.type === 'total');

    // Calculate button state
    const disableBookButton =
        (!hasChosenDate && attemptedBooking) ||
        (hasMultipleProducts && !hasChosenDate) ||
        errors.length > 0;

    // Activity-specific warning label logic
    function getWarningLabel() {
        if (!hasChosenDate || (hasMultipleProducts && !hasChosenDate)) return undefined;
        if (errors.some((e) => e.error === 'minEntrants')) {
            return t.a_minimum_of_participants_is_required_to_book_this_product;
        }
        if (errors.some((e) => e.error === 'capacity')) {
            return t.no_available_capacity_for_this_tour;
        }
        return undefined;
    }

    const shouldDisplayTime =
        (products.length > 1 || products[0]?.showTime) && !(hideTime && !hasMultipleProducts);

    return (
        <Stack alignItems="center" gap={2} textAlign="left" sx={{ width: '100%' }}>
            <>
                {hasAvailableCapacity ? (
                    <Fragment>
                        <Box
                            sx={{
                                width: '100%',
                                '&& > *': { color: customizations.productCardTextColor },
                            }}
                        >
                            <InlineTimeslotTitle
                                selectedTimeSlot={selectedTimeSlot}
                                hasMultipleProducts={hasMultipleProducts}
                                displayDate={displayDate}
                                showTime={shouldDisplayTime}
                                displayTimeBorder={displayTimeBorder}
                                onClick={() => {
                                    if (hasMultipleProducts) {
                                        setCurrentInteraction('SELECT_TIME');
                                        bookingDispatch(setSelectedTimeSlot(undefined));
                                        bookingDispatch(setHasChosenDate(false));
                                    }
                                }}
                            />
                            <Collapse
                                easing="linear(0,1)"
                                in={currentInteraction === 'SELECT_TIME'}
                                exit={currentInteraction !== 'SELECT_TIME'}
                            >
                                <InstancePicker
                                    selectedTimeSlot={selectedTimeSlot}
                                    onSelectTimeSlot={(ts: TimeSlotType) => {
                                        bookingDispatch(setSelectedTimeSlot(ts));
                                        bookingDispatch(setSelectedProducts([ts.product]));
                                        bookingDispatch(
                                            setQuantities(
                                                ts.product.ticketOptions.map((ticket) => ({
                                                    ...ticket,
                                                    quantity: 0,
                                                })),
                                            ),
                                        );
                                        bookingDispatch(setHasChosenDate(true));
                                        setCurrentInteraction('SELECT_PARTICIPANTS');
                                    }}
                                    availabilityData={products}
                                    hideHeader
                                    fullWidth
                                    color={customizations.productCardTextColor}
                                    backgroundColor={customizations.productCardColor}
                                    primaryColor={customizations.productCardPrimaryColor}
                                    primaryColorContrast={
                                        customizations.productCardPrimaryColorContrast
                                    }
                                />
                            </Collapse>
                            <Collapse
                                easing="linear(0,1)"
                                in={currentInteraction === 'SELECT_PARTICIPANTS'}
                                exit={currentInteraction !== 'SELECT_PARTICIPANTS'}
                            >
                                {ticketTypes && ticketTypes.length > 0 && (
                                    <Dropdown
                                        value={selectedTicketType ?? null}
                                        options={ticketTypes}
                                        getOptionLabel={(opt) => opt?.name ?? t.select_price_type}
                                        onChange={(opt) => {
                                            bookingDispatch(
                                                setSelectedTicketType?.(opt ?? undefined),
                                            );
                                        }}
                                        labelColor={customizations.bookingWidgetColorContrast}
                                        label={t.select_price_type}
                                        error={
                                            attemptedBooking && !selectedTicketType
                                                ? t.please_select_price_type
                                                : undefined
                                        }
                                    />
                                )}
                                <MultipleNumberInput
                                    labelText=""
                                    items={inputItems}
                                    onChange={onChangeQuantity}
                                    globalError={
                                        totalErrors.length > 0 ? getWarningLabel() : undefined
                                    }
                                    labelColor={customizations.productCardTextColor}
                                    backgroundColor={customizations.productCardColor}
                                    color={customizations.productCardTextColor}
                                    variant="filled"
                                    border={`1px solid ${alpha(
                                        customizations.productCardTextColor,
                                        0.2,
                                    )}`}
                                />

                                <Box sx={{ mb: 0.575 }}></Box>
                            </Collapse>
                        </Box>
                        <WarningMessage label={getWarningLabel()} />
                    </Fragment>
                ) : (
                    <Box sx={{ py: 6.8 }}></Box>
                )}
                <InlineSummary
                    onBook={onBook}
                    quantities={priceQuantities}
                    fromPrice={products[0].product?.fromPrice ?? null}
                    hasCapacity={hasAvailableCapacity}
                    bookingDisabled={!hasChosenDate || !selectedProducts || disableBookButton}
                    totalPriceOverride={discountedTotal}
                    isLoadingPrices={isLoadingPrices || isCoverageLoading}
                    isWaitingList={
                        selectedProducts?.some(
                            (product) => product.hasWaitingList && product.isClosedForBooking,
                        ) ?? false
                    }
                />
                {inCart && <InCartIndicator />}
            </>
        </Stack>
    );
}

type InlineTimeslotTitleProps = {
    displayDate?: boolean;
    showTime?: boolean;
    selectedTimeSlot: TimeSlotType | undefined;
    hasMultipleProducts: boolean;
    onClick: () => void;
    displayTimeBorder?: boolean;
};

function InlineTimeslotTitle({
    selectedTimeSlot,
    hasMultipleProducts,
    onClick,
    showTime = true,
    displayDate,
    displayTimeBorder,
}: InlineTimeslotTitleProps) {
    const { t } = useLocale();
    const customizations = useCustomizations();
    if (!showTime) return null;
    return (
        <Typography
            sx={{
                cursor: 'pointer',
                fontSize: '0.9rem',
                mb: 1.5,
                pb: 0.5,
                lineHeight: 1,
                borderBottom: displayTimeBorder ? '1px solid #00000044' : undefined,
            }}
            onClick={onClick}
        >
            <>
                {displayDate && selectedTimeSlot?.product.start && (
                    <Stack component="span" direction="row" alignItems="center">
                        <Box component="span" fontWeight="bold">
                            {t.date}: {selectedTimeSlot.product.start.format('Do MMM')}
                        </Box>
                    </Stack>
                )}
                {selectedTimeSlot ? (
                    <Stack component="span" direction="row" alignItems="center" fontWeight="bold">
                        <Box component="span" mr={0.5}>
                            {hasMultipleProducts ? t.selected_time : t.time}:
                        </Box>
                        <span>{` ${tzdate(selectedTimeSlot?.product.start).format(
                            'HH:mm',
                        )} - ${tzdate(selectedTimeSlot?.product.end).format('HH:mm')}`}</span>
                        {hasMultipleProducts && (
                            <EditIcon
                                sx={{
                                    ml: 'auto',
                                    fontSize: '1rem',
                                    color: customizations.productCardTextColor,
                                }}
                            />
                        )}
                    </Stack>
                ) : (
                    <span style={{ fontWeight: 'bold' }}>{t.select_time}</span>
                )}
            </>
        </Typography>
    );
}
